import React, { useLayoutEffect, useRef, useState, useEffect } from "react"
import { Link } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/dist/ScrollTrigger"
import References from "@components/References"
import ColorLines from "@homepage/ColorLines"
import Callout from "@components/Callout"
import Circle from "@homepage/Circle"
import ModalExit from "@components/ModalExit"
import Container from "@structure/Container"

import tw, { css } from "twin.macro"
import {
  btnStyles,
  darkPurpleHeading,
  purpleHeading,
  paragraphStyles,
} from "@styles/ui"

import Anchor from "@images/homepage/anchor.inline.svg"

const SectionThreePartTwo = ({ videoFPOThumbnail, icon, icon2, location }) => {
  gsap.registerPlugin(ScrollTrigger)

  const callout = useRef(null)
  const resources = useRef(null)
  const resourceCenter = useRef(null)
  const resourceCenterH2 = useRef(null)
  const usingDefencath = useRef(null)
  const usingDefencathH2 = useRef(null)
  const resourcesBg = useRef(null)
  const everythingContainer = useRef(null)
  const referencesRef = useRef(null)
  const tanInnerContentHolder = useRef(null)

  const [udCircleTop, setUdCircleTop] = useState("0px")

  const pStyles = [paragraphStyles, tw`text-black mb-4`]
  const buttonStyle = [btnStyles, tw`transition-all duration-300`]

  const referencesContent = [
    <>
      {" "}
      DefenCath<sup>&reg;</sup> (taurolidine and heparin) catheter lock solution
      Prescribing Information, CorMedix, Berkeley Heights, New Jersey.
    </>,
    <>
      Agarwal AK, Roy-Chaudhury P, Mounts P, Hurlburt E, Pfaffle A, Poggio EC.
      Taurolidine/heparin lock solution and catheter-related bloodstream
      infection in hemodialysis: a randomized, double-blind, active-control,
      phase 3 study. <i>Clin J Am Soc Nephrol</i>. 2023 Nov 1;18(11):1446-1455.
      doi:10.2215/CJN.0000000000278
    </>,
    <>
      Arechabala MC, Catoni MI, Claro JC, et al. Antimicrobial lock solutions
      for preventing catheter-related infections in haemodialysis.{" "}
      <span tw="italic">Cochrane Database Syst Rev.</span> 2018;(4):CD010597.
      doi:10.1002/14651858.CD010597.pub2
    </>,
  ]

  useEffect(() => {
    setUdCircleTop(`${usingDefencathH2.current.offsetTop}px`)
  }, [usingDefencathH2])

  useLayoutEffect(() => {
    let mm = gsap.matchMedia()

    const ctx = gsap.context(() => {
      mm.add("(max-width: 1023px)", () => {
        ScrollTrigger.create({
          trigger: usingDefencathH2.current,
          start: "top-=150px top",
          end: "bottom-=172px top",
          endTrigger: resourceCenterH2.current,
          pin: '#last-purple-helper-container div[data-id="circle"]',
          pinSpacing: false,
          scrub: 1,
          toggleActions: "restart none none reverse",
        })
      })

      mm.add("(min-width: 1024px)", () => {
        gsap.fromTo(
          tanInnerContentHolder.current,
          { overflow: "hidden" },
          {
            overflow: "unset",
            duration: 1.5,
            ease: "sine.out",
            scrollTrigger: {
              trigger: tanInnerContentHolder.current,
              start: "top 25%",
              end: "top 25%",
              toggleActions: "restart none none reverse",
            },
          }
        )

        gsap.fromTo(
          resourcesBg.current,
          {
            transform: "scale(1)",
            duration: 2,
          },
          {
            transform: "scale(3)",
            duration: 20,
            ease: "sine.inOut",
            scrollTrigger: {
              trigger: tanInnerContentHolder.current,
              start: "top 25%",
              end: "100px 15%",
              scrub: 1,
              toggleActions: "restart none none reverse",
            },
          }
        )

        ScrollTrigger.create({
          trigger: "#last-purple-helper-container",
          start: "top-=30% top",
          end: "bottom-=45% top",
          pin: '#last-purple-helper-container div[data-id="circle"]',
          pinSpacing: false,
          scrub: 1,
          toggleActions: "restart none none reverse",
        })
      })
    })

    return () => ctx.revert()
  }, [])

  return (
    <div tw="relative">
      <div
        id="sectionThreePartTwo"
        className="hp-sections"
        ref={everythingContainer}
        tw="relative w-full"
      >
        <section
          ref={resources}
          id="resources"
          tw="bg-tan h-auto rounded-[12px] relative w-full z-10 mb-0 md:rounded-[40px] lg:(mt-0 mx-auto bottom-0 left-0) xl:(max-w-[1200px] m-auto ) 2xl:(rounded-[53px])"
        >
          <div
            id="anchor-two"
            tw="absolute z-10 w-[101vw] h-[101.6vw] max-w-[353px] -top-[35vh] -right-[30vw] sm:(mr-[50px] -mt-[155px] -top-[20vh] w-[400px] max-w-[400px]) md:(mr-[120px] -mt-[185px]) lg:(mr-[40px] mt-[20px] h-[700px] w-[600px] max-w-[600px] -right-[8rem] -top-[27rem]) xl:(mr-0 mt-0 w-[650px] max-w-[650px] -top-[30rem]) 2xl:(-right-[18rem]) 3xl:(w-[753px] max-w-[753px] -top-[35rem] -right-[19rem])"
            ref={icon2}
          >
            <Anchor tw="absolute w-full h-auto" />
          </div>

          <div
            className="tan-background"
            ref={resourcesBg}
            tw="absolute m-auto right-0 top-0 left-0 bg-tan lg:(block w-full h-full rounded-[40px]) 2xl:(rounded-[53px])"
          ></div>

          <div
            data-screenshot="section-three-part-two"
            ref={tanInnerContentHolder}
            tw="overflow-hidden pt-5 md:pt-10 lg:(pb-10 pt-24 m-auto relative) xl:(pb-0 max-w-[1200px])"
          >
            <div tw="relative z-[1] pl-[3.188rem] pr-4 lg:(pl-0  pr-0 z-10)">
              <div
                className="tan-panel"
                ref={usingDefencath}
                id="using-defencath"
                tw="relative pb-[74px] md:pb-[110px] lg:pb-[176px] xl:pb-[206px]"
              >
                <div
                  id="last-purple-helper-container"
                  tw="h-full top-0 -left-[36.58px] z-10 w-5 absolute lg:(m-auto left-[0.5px] right-0 bottom-0)"
                >
                  <ColorLines
                    purple
                    tw="h-[150vh] absolute left-1.5 -bottom-2 lg:bottom-14 [div.lines]:(mobileOnly:(mr-[1.5px]) mr-auto)"
                  />
                  <Circle
                    css={[
                      css`
                        top: ${udCircleTop};
                      `,
                      tw`bg-light-purple absolute left-0`,
                    ]}
                  />
                </div>

                <div
                  data-isi-trigger="contraindications"
                  tw="mt-0 grid lg:(h-auto grid-cols-12 gap-6)"
                >
                  <div tw="lg:(pl-10 pr-8 col-start-1 col-span-6 max-w-full)">
                    <GatsbyImage
                      alt=""
                      loading="eager"
                      objectFit="contain"
                      image={videoFPOThumbnail}
                      tw="mobileOnly:(!w-full aspect-[145/81] !h-auto order-first rounded-xl)"
                    />
                  </div>

                  <div tw="pr-10 lg:(col-start-8 col-span-4 pr-0 pt-6 pb-8)">
                    <h2
                      css={[purpleHeading, tw`mt-4 lg:(mt-0 mb-3)`]}
                      ref={usingDefencathH2}
                    >
                      USING DEFENCATH
                    </h2>

                    <h3
                      css={[
                        darkPurpleHeading,
                        tw`md:pb-2 lg:(pb-0 text-[37.5px]) xl:(text-5xl)`,
                      ]}
                    >
                      Can be utilized with commonly used HD-CVCs
                    </h3>

                    <p css={pStyles}>
                      DefenCath, with refrigeration-free storage, can be
                      utilized with commonly used HD-CVCs and is administered
                      similarly to standard-of-care (heparin).
                      <sup>1,3</sup>
                    </p>
                    <div tw="my-5 lg:(my-6)">
                      <Link
                        to="/dosing-and-administration"
                        css={[buttonStyle, tw`text-tan md:(block w-[200px])`]}
                      >
                        Learn how to use
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="tan-panel"
                ref={resourceCenter}
                id="defencath-resource-center"
              >
                <div>
                  <div tw="pr-8 max-w-[870px] lg:(pr-0 m-auto)">
                    <h2
                      css={[purpleHeading, tw`lg:(text-center mb-3)`]}
                      ref={resourceCenterH2}
                    >
                      DEFENCATH RESOURCE PORTAL
                    </h2>

                    <h3
                      css={[
                        darkPurpleHeading,
                        tw`md:pb-2 lg:(pb-0 text-[32px] text-center) xl:(text-5xl)`,
                      ]}
                    >
                      Get information and helpful&nbsp;downloads about
                      <br tw="hidden lg:(block)" /> DefenCath
                      <sup tw="text-[50%] top-[-0.7em]">&reg;</sup>
                      <br tw="hidden lg:(block)" /> (taurolidine and heparin)
                      <br tw="hidden lg:(block)" /> catheter lock solution
                    </h3>

                    <p
                      css={[pStyles, tw`max-w-[500px] lg:(m-auto text-center)`]}
                    >
                      Discover resources and tools for protocol adoption,
                      implementation best practices, ordering information,
                      reimbursement, and more.
                    </p>

                    <div tw="my-5 lg:(flex justify-center mb-8)">
                      <ModalExit
                        triggerContent={"Access resources"}
                        triggerStyles={[
                          buttonStyle,
                          tw`text-tan md:(block w-[200px])`,
                        ]}
                        destinationUrl="https://www.defencathresources.com/"
                      />
                    </div>
                  </div>

                  <div ref={referencesRef} tw="w-full pr-10 [div]:px-0 lg:pr-0">
                    <References references={referencesContent} />
                  </div>

                  <div
                    ref={callout}
                    id="bottom-hero-callout"
                    tw="mobileOnly:-ml-[30px]"
                  >
                    <aside tw="mobileOnly:([div.pagination-callout]:px-0) pt-[115px] pb-5 px-0 lg:(pt-[120px])">
                      <Callout
                        shortHeight
                        subtitle="Next: Proven Efficacy"
                        title={
                          <>
                            See how DefenCath performed against
                            <br tw="md:hidden" /> heparin.
                          </>
                        }
                        buttontxt="Explore efficacy"
                        href="/efficacy"
                      />
                    </aside>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default SectionThreePartTwo
